import React, {
  Fragment,
  useState,
  useContext,
  useEffect,
} from "react";
import { NavLink, useNavigate } from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  InputAdornment,
  IconButton,
  TextField,
  Divider,
  Container,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import SideBanner from "../components/layout/sidebanner";
import AuthContext from "../store/auth/auth-context";
import Cookie from "js-cookie";


let loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .matches(/@[^.]*\./,"Please enter a valid email address").required("Please enter email address")
    .max(256, "Email address should have maximum of 256 characters"),
  password: yup.string().required("Please enter password"),
});

const Login = () => {
  const authCxt = useContext(AuthContext);
  const navigate = useNavigate()
  const accessToken = Cookie.get("accessToken");
  const refreshToken = Cookie.get("refreshToken");

  useEffect(() => {
    if (accessToken && refreshToken) navigate("/");    
  }, [authCxt]);

  const {
    register: userLogin,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show: any) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onSubmit = (data: any) => {
    if (data.email && data.password) {
      authCxt.login(data);
    }
  };

  return (
    <Fragment>
      <Container
        component="main"
        maxWidth={false}
        disableGutters
        sx={{ height: "100vh" }}
      >
        <Box
          sx={{
            margin: 0,
            height: "100vh",
          }}
        >
          <Grid container>
            <CssBaseline />

            <Grid
              item
              sm={5}
              // spacing={2}
              sx={{
                display: { xs: "none", sm: "flex", lg: "flex" },
                backgroundImage: "url(/assets/Elements/Login-side-panel.png)",
                backgroundRepeat: "no-repeat",
                backgroundColor: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.grey[50]
                    : t.palette.grey[900],
                backgroundSize: "cover",

                backgroundPosition: "center",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SideBanner></SideBanner>
            </Grid>

            <Grid
              item
              xs={12}
              sm={7}
              // md={0}
              component={Paper}
              // elevation={6}
              square
            >
              <Grid
                sx={{
                  mx: 4,
                  display: "flex",
                  height: "100vh",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "Space-between",
                }}                
              >
                <Grid>
                  <Typography variant="body2"> </Typography>
                </Grid>
                <Grid sx={{ width: "100%" }} xs={12} sm={10} md={8}>
                  
                  <Box
                    mt={3}
                    component="form"
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <Typography component="h5" variant="h5">
                      Login
                    </Typography>
                    <Typography
                      variant="body2"
                      mb={2}
                      mt={1}
                      sx={{ color: authCxt.isAuthError===true ? "#FF0606" : "#C1C1C1" }}
                    >
                      {authCxt.isAuthError===true
                        ? authCxt.authMessage//"Please enter valid email address and password"
                        : "Enter your credentials to access your account"}
                    </Typography>

                    <Divider light={true} />

                    <Typography variant="subtitle2" mt={2} mb={1}>
                      Email Address
                    </Typography>
                    <TextField
                      error={Boolean(errors.email)}
                      helperText={errors.email ? errors.email.message : " "}
                      required
                      {...userLogin("email")}
                      fullWidth
                      placeholder="name@company.com"
                      size="small"
                      id="email"
                      autoComplete="email"
                      autoFocus
                      sx={{ mb: 0 }}
                    />

                    <Typography variant="subtitle2" mb={1}>
                      Password
                    </Typography>
                    <TextField
                      error={Boolean(errors.password)}
                      variant="outlined"
                      helperText={
                        errors.password ? errors.password.message : " "
                      }
                      required
                      fullWidth
                      placeholder="************"
                      {...userLogin("password")}
                      type={showPassword ? "text" : "password"}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff
                                  sx={{ color: "#8D8D8D", fontSize: "1.2rem" }}
                                />
                              ) : (
                                <Visibility
                                  sx={{ color: "#8D8D8D", fontSize: "1.2rem" }}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Grid
                      container
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Grid item xs>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              value="remember"
                              color="primary"
                            />
                          }
                          label={
                            <Box component="div" fontSize={"12px"}>
                              Remember me
                            </Box>
                          }
                          sx={{ color: "#8D8D8D", fontSize: ".7rem" }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">
                          <NavLink to="/forgotpassword">Forgot password?</NavLink>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={authCxt.isLoading}
                        sx={{
                          mt: 3,
                          mb: 2,
                          width: "250px",
                          bgcolor: "#F47907",
                          fontSize: "11px",
                          "&.Mui-disabled": {
                            background: "#f6d9b9",
                          },
                          "&:hover":{
                            bgcolor: "#FF8D24",
                          }
                        }}
                      >
                        Login
                      </LoadingButton>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Typography variant="body2" sx={{ color: "gray" }}>
                        {"Don't have a SenseOps account? "}
                        <NavLink to="/signup">Sign up</NavLink>
                      </Typography>
                    </Box>
                  </Box>

                </Grid>

                <Grid mb={2}>
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    If you're facing any issues on this page, Please{" "}
                    <NavLink to="#">contact us</NavLink>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Fragment>
  );
};

export default Login;
